import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { notificationsAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { createConsumer } from '@rails/actioncable';

const NotificationContext = createContext();

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export const NotificationProvider = ({ children }) => {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const { user } = useAuth();
  const cableRef = useRef(null);
  const subscriptionRef = useRef(null);

  // Fetch notifications when user is logged in
  const fetchNotifications = useCallback(async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const response = await notificationsAPI.getAll();
      const { notifications: fetchedNotifications, unread_count } = response.data;
      
      setNotifications(fetchedNotifications);
      setUnreadCount(unread_count);
    } catch (error) {
      console.error('Error fetching notifications:', error);
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    if (!user) {
      // Cleanup when logging out
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (cableRef.current) {
        cableRef.current.disconnect();
        cableRef.current = null;
      }
      setNotifications([]);
      setUnreadCount(0);
      return;
    }

    // Initial fetch
    fetchNotifications();

    // Initialize Action Cable connection with JWT token
    const token = localStorage.getItem('token');
    const protocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    
    // Extract hostname from API base URL (remove protocol if present)
    let host;
    if (window.location.hostname === 'localhost') {
      host = 'localhost:3000';
    } else {
      const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || '';
      // Remove protocol (http://, https://) if present
      host = apiBaseUrl.replace(/^https?:\/\//, '');
    }
    
    const defaultUrl = `${protocol}://${host}/cable`;
    const cableUrl = `${defaultUrl}?token=${token}`;
    cableRef.current = createConsumer(cableUrl);

    subscriptionRef.current = cableRef.current.subscriptions.create(
      { channel: 'NotificationsChannel' },
      {
        received: (data) => {
          // Push newest notification to top
          setNotifications(prev => [data, ...prev]);
          if (!data.read) {
            setUnreadCount(prev => prev + 1);
          }
        },
      }
    );

    // Cleanup on unmount or user change
    return () => {
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
      if (cableRef.current) {
        cableRef.current.disconnect();
        cableRef.current = null;
      }
    };
  }, [user, fetchNotifications]);

  const markAsRead = async (notificationId) => {
    try {
      await notificationsAPI.markAsRead(notificationId);
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => 
          notification.id === notificationId 
            ? { ...notification, read: true }
            : notification
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsAPI.markAllAsRead();
      
      // Update local state
      setNotifications(prev => 
        prev.map(notification => ({ ...notification, read: true }))
      );
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const addNotification = (notification) => {
    setNotifications(prev => [notification, ...prev]);
    if (!notification.read) {
      setUnreadCount(prev => prev + 1);
    }
  };

  const getNotificationIcon = (type) => {
    switch (type) {
      case 'order_placed':
        return '🛒';
      case 'order_confirmed':
        return '✅';
      case 'order_shipped':
        return '🚚';
      case 'order_delivered':
        return '📦';
      case 'order_cancelled':
        return '❌';
      case 'admin_new_order':
        return '📋';
      default:
        return '🔔';
    }
  };

  const getNotificationColor = (type) => {
    switch (type) {
      case 'order_placed':
        return 'bg-blue-50 border-blue-200';
      case 'order_confirmed':
        return 'bg-green-50 border-green-200';
      case 'order_shipped':
        return 'bg-yellow-50 border-yellow-200';
      case 'order_delivered':
        return 'bg-green-50 border-green-200';
      case 'order_cancelled':
        return 'bg-red-50 border-red-200';
      case 'admin_new_order':
        return 'bg-purple-50 border-purple-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const value = {
    notifications,
    unreadCount,
    loading,
    fetchNotifications,
    markAsRead,
    markAllAsRead,
    addNotification,
    getNotificationIcon,
    getNotificationColor
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
